import type { ReactNode } from 'react';
import { PopoverDefinition } from '../PopoverDefinition/PopoverDefinition';
import { EquipmentOwner } from './EquipmentOwner';
import type { OwnershipRow } from './OwnershipRow';
import styles from './ResponsibilityTable.module.scss';

function EquipmentLabelCell({ row }: { row: OwnershipRow }): ReactNode {
	return (
		<td className={styles['owned']}>
			<PopoverDefinition content={row.description} zIndex={12000}>
				{row.name}
			</PopoverDefinition>
		</td>
	);
}

function ResponsibilityHeadings({ data }: { data: readonly OwnershipRow[] }): ReactNode {
	const hasDTEOwned = data.some((row) => row.owner === EquipmentOwner.DTE);
	const hasOtherUtilityOwned = data.some((row) => row.owner === EquipmentOwner.OtherUtility);
	const hasCustomerOwned = data.some((row) => row.owner === EquipmentOwner.Customer);

	return (
		<thead>
			<tr>
				{hasDTEOwned && <th scope="col">Utility (DTE)</th>}
				{hasOtherUtilityOwned && <th scope="col">Other Utilities</th>}
				{hasCustomerOwned && <th scope="col">Customer</th>}
			</tr>
		</thead>
	);
}

function DTEResponsibilityBody({ data }: { data: readonly OwnershipRow[] }): ReactNode {
	const dteOwnedRows = data.filter((row) => row.owner === EquipmentOwner.DTE);
	const otherUtilityOwnedRows = data.filter((row) => row.owner === EquipmentOwner.OtherUtility);
	const customerOwnedRows = data.filter((row) => row.owner === EquipmentOwner.Customer);

	if (!dteOwnedRows?.length) {
		return null;
	}

	return (
		<tbody className={styles['dteOwned']}>
			{dteOwnedRows.map((row) => (
				<tr key={row.name}>
					<EquipmentLabelCell row={row} />
					{otherUtilityOwnedRows.length > 0 && <td>&nbsp;</td>}
					{customerOwnedRows.length > 0 && <td>&nbsp;</td>}
				</tr>
			))}
		</tbody>
	);
}

function OtherUtilityResponsibilityBody({ data }: { data: readonly OwnershipRow[] }): ReactNode {
	const dteOwnedRows = data.filter((row) => row.owner === EquipmentOwner.DTE);
	const otherUtilityOwnedRows = data.filter((row) => row.owner === EquipmentOwner.OtherUtility);
	const customerOwnedRows = data.filter((row) => row.owner === EquipmentOwner.Customer);

	if (!otherUtilityOwnedRows?.length) {
		return null;
	}

	return (
		<tbody className={styles['otherUtilityOwned']}>
			{otherUtilityOwnedRows.map((row) => (
				<tr key={row.name}>
					{dteOwnedRows.length > 0 && <td>&nbsp;</td>}
					<EquipmentLabelCell row={row} />
					{customerOwnedRows.length > 0 && <td>&nbsp;</td>}
				</tr>
			))}
		</tbody>
	);
}
function CustomerResponsibilityBody({ data }: { data: readonly OwnershipRow[] }): ReactNode {
	const dteOwnedRows = data.filter((row) => row.owner === EquipmentOwner.DTE);
	const otherUtilityOwnedRows = data.filter((row) => row.owner === EquipmentOwner.OtherUtility);
	const customerOwnedRows = data.filter((row) => row.owner === EquipmentOwner.Customer);

	if (!customerOwnedRows?.length) {
		return null;
	}

	return (
		<tbody className={styles['customerOwned']}>
			{customerOwnedRows.map((row) => (
				<tr key={row.name}>
					{dteOwnedRows.length > 0 && <td>&nbsp;</td>}
					{otherUtilityOwnedRows.length > 0 && <td>&nbsp;</td>}
					<EquipmentLabelCell row={row} />
				</tr>
			))}
		</tbody>
	);
}

export function ResponsibilityTable({ data }: { data: readonly OwnershipRow[] }): ReactNode {
	return (
		<table className={styles['responsibilityTable']}>
			<ResponsibilityHeadings data={data} />
			<DTEResponsibilityBody data={data} />
			<OtherUtilityResponsibilityBody data={data} />
			<CustomerResponsibilityBody data={data} />
		</table>
	);
}
